/**
 * "View as user" (doAs) transport state.
 *
 * A SarasAdmin can read a dashboard AS a chosen end user: iq-backend runs the
 * whole request under the target user's identity when it carries
 * `X-Do-As-User-Id: <numeric userId>` alongside the admin's own Bearer token.
 *
 * The state of record for the mode lives in `ViewAsUserContext` (React state, so
 * the UI reacts to it). This module is the tiny SYNCHRONOUS mirror the service
 * layer reads — the API modules and the raw-`fetch` SSE transport build their
 * requests outside React and can't call hooks, exactly like `getCookie` for the
 * auth token. The context is the only writer; everything else reads.
 *
 * In-memory only: the mode never survives a reload, which is deliberate — an admin
 * must re-arm impersonation explicitly rather than find themselves in it.
 *
 * SCOPING RULE — opt-in at the call site, not a URL allowlist. Only the dashboard
 * data READ functions attach the header, by spreading `doAsHeaders()` into their
 * own request. Nothing does it for them: there is no interceptor rule and no
 * pattern match on the URL, so a new endpoint carries the header only if someone
 * writes that spread on purpose. A MUTATION must never call `doAsHeaders()` —
 * impersonating a share, a cache purge, an SQL clear, a metrics write or an
 * unassign would be a real privilege bug.
 *
 * The opted-in reads today:
 *  - `getDashboard`            — `GET  /dashboards/{id}`
 *  - `fetchUseCaseDashboard`   — `GET  /use-cases/{u}/dashboards/{d}`
 *  - `streamDashboardQueries`  — `POST /dashboards/queries/execute` (owner) and
 *                                `POST /dashboards/{id}/queries/execute` (shared)
 *
 * Why the call site and not a URL allowlist: dashboard ids are opaque hex strings
 * now, so any id-shaped pattern silently stopped matching and the header quietly
 * went missing. URL shape also cannot tell a read from a mutation —
 * `DELETE /use-cases/{u}/dashboards/{d}` (unassign) is byte-identical to the
 * use-case spec read. The call site knows both, and a missing spread is a visible
 * absence in the read function rather than an invisible regex near-miss.
 */

import { HEADERS } from "@/constants";

let viewAsUserId: number | null = null;

/**
 * Sets (or clears with `null`) the user every dashboard data request is executed
 * as. Called ONLY by `ViewAsUserProvider`.
 *
 * @example setViewAsUserId(4821)  // subsequent dashboard reads run as user 4821
 * @example setViewAsUserId(null)  // back to the admin's own identity
 */
export function setViewAsUserId(userId: number | null): void {
  viewAsUserId = userId != null && Number.isFinite(userId) ? userId : null;
}

/** The impersonated user id, or `null` when the admin is browsing as themselves. */
export function getViewAsUserId(): number | null {
  return viewAsUserId;
}

/**
 * The header pair to spread into a request while the mode is active, `{}` when the
 * admin is browsing as themselves. This is the ONLY way the doAs header reaches
 * the wire: every opted-in read spreads it into its own headers, so an endpoint
 * that never calls this can never impersonate.
 *
 * @example headers: { ...doAsHeaders() }   // -> { "X-Do-As-User-Id": "4821" }
 */
export function doAsHeaders(): Record<string, string> {
  const id = getViewAsUserId();
  if (id == null) return {};
  return { [HEADERS.DO_AS_USER]: String(id) };
}
